import { Service as AppShellService } from '@buffer-mono/app-shell/src/exports/Orchestrator/channelConnections/types'

export { AccountType } from '@buffer-mono/app-shell/src/common/events/types'

/**
 * A service the app-shell orchestrator can run a connection flow for, as a
 * plain string value. Note this is narrower than the GraphQL `Service` enum:
 * `startPage` has no orchestrator connection flow.
 */
export type ConnectableService = `${AppShellService}`

const APP_SHELL_SERVICES: Record<ConnectableService, AppShellService> = {
  bluesky: AppShellService.bluesky,
  facebook: AppShellService.facebook,
  googlebusiness: AppShellService.googlebusiness,
  instagram: AppShellService.instagram,
  instagram_login_api: AppShellService.instagram_login_api,
  linkedin: AppShellService.linkedin,
  mastodon: AppShellService.mastodon,
  pinterest: AppShellService.pinterest,
  substack: AppShellService.substack,
  threads: AppShellService.threads,
  tiktok: AppShellService.tiktok,
  twitter: AppShellService.twitter,
  whatsapp: AppShellService.whatsapp,
  youtube: AppShellService.youtube,
}

function isConnectableService(value: string): value is ConnectableService {
  return Object.hasOwn(APP_SHELL_SERVICES, value)
}

/**
 * Narrows a loosely typed service name (GraphQL enum value, legacy Redux
 * state, composer service name) to a service the orchestrator can connect.
 * Case-insensitive; returns `null` for services without a connection flow
 * (e.g. `startPage`).
 */
export function toConnectableService(
  service: string,
): ConnectableService | null {
  const normalized = service.toLowerCase()
  return isConnectableService(normalized) ? normalized : null
}

/**
 * Whether the app-shell orchestrator is loaded and can run channel connection
 * flows. Callers with a fallback path (e.g. save-and-redirect when the shell
 * bundle failed to load) should check this before calling `connectChannel`;
 * without a check, `connectChannel` silently no-ops when the shell is missing.
 */
export function isChannelConnectionAvailable(): boolean {
  return window.appshell?.actions?.connectChannel != null
}

export type ConnectChannelOptions = {
  service: ConnectableService
  cta?: string
  name?: string
  destinationUrl?: string
  isRefreshingConnection?: boolean
  selectedRefreshChannelId?: string
  customChannelIdToConvert?: string
  authMethod?: string
  skipCelebrationModal?: boolean
  skipConfigurationModal?: boolean
  prefillDataAfterConnection?: boolean
  /**
   * Whether the current user can connect channels (the organization
   * `manageChannels` allowed action). When `false`, the channel storefront
   * opens instead of the connection flow.
   */
  canManageChannels?: boolean
}

/**
 * Starts the app-shell channel connection flow (new connection, reconnection
 * or refresh, or custom-channel conversion). No-ops when the shell is not
 * loaded — see `isChannelConnectionAvailable`.
 */
export function connectChannel({
  service,
  canManageChannels = true,
  ...options
}: ConnectChannelOptions): void {
  const selectedService = APP_SHELL_SERVICES[service]

  if (!canManageChannels) {
    window.appshell?.actions?.openChannelStorefront?.({
      cta: options.cta ?? 'connectChannel-opened-as-non-admin',
      selectedService,
    })
    return
  }

  window.appshell?.actions?.connectChannel?.({
    selectedService,
    ...options,
  })
}

export type OpenChannelStorefrontOptions = {
  cta: string
  skipCelebrationModal?: boolean
  prefillDataAfterConnection?: boolean
}

/**
 * Opens the app-shell channel storefront, where the user picks a service to
 * connect. No-ops when the shell is not loaded.
 */
export function openChannelStorefront(
  options: OpenChannelStorefrontOptions,
): void {
  window.appshell?.actions?.openChannelStorefront?.(options)
}
